<template>
  <!--
    その社員が打刻できる場所（見るだけ）と、その社員を「指定エリア外での打刻制限」から外すスイッチ。

    どこから見えるか：ユーザー管理 → ユーザー編集 → 画面の下のほう。
    場所の追加・変更は、自社設定の「打刻場所の登録」1か所だけで行う（ここには登録の入口を置かない。
    同じ場所を2か所から登録できると、どちらが効いているのか分からなくなる）。

    位置情報記録を使っていない会社で、場所も1件も無いときは、欄そのものを出さない。
    読めなかったとき（権限が無い・通信の失敗）も出さない。ユーザー編集の本体には響かせない。
  -->
  <section v-if="visible" class="user-stamping-places" data-testid="user-stamping-places">
    <h3>{{ T.userTitle }}</h3>
    <p class="user-stamping-places__lead">{{ T.userDescription }}</p>

    <p v-if="!places.length" class="user-stamping-places__empty" data-testid="user-stamping-places-empty">{{ T.userEmpty }}</p>
    <div v-else class="user-stamping-places__table">
      <table>
        <thead>
          <tr>
            <th>{{ T.columnName }}</th>
            <th>{{ T.userColumnKind }}</th>
            <th>{{ T.columnAddress }}</th>
            <th>{{ T.columnRadius }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="place in places" :key="place.id" :data-testid="'user-stamping-place-' + place.id">
            <td>{{ place.name }}</td>
            <td>{{ kindOf(place) }}</td>
            <td>
              {{ addressOf(place) }}
              <a :href="mapUrl(place)" target="_blank" rel="noopener noreferrer">地図を表示</a>
            </td>
            <td>{{ place.radius_m }}m</td>
          </tr>
        </tbody>
      </table>
    </div>

    <label class="user-stamping-places__switch" for="user-stamping-exempt">
      <input id="user-stamping-exempt" v-model="exempt" type="checkbox" :disabled="busy"/>
      <span>
        <strong>{{ T.exemptName }}</strong>
        <small>{{ T.exemptDescription }}</small>
        <small v-if="!restrictEnabled" data-testid="user-stamping-exempt-note">{{ T.exemptNotActive }}</small>
      </span>
    </label>

    <p v-if="message" class="user-stamping-places__message" :class="{ 'user-stamping-places__message--error': failed }" role="status" data-testid="user-stamping-message">{{ message }}</p>
    <button
      type="button"
      class="btn btn-primary"
      data-testid="user-stamping-save"
      :disabled="busy || exempt === savedExempt"
      @click="save"
    >{{ T.save }}</button>
  </section>
</template>

<script>
import axios from 'axios'
import T from '@/setting/stampingPlaceTexts'
import { backendErrorMessage } from '@/utils/rosterActualPolicy'
import { stampPlaceName } from '@/utils/stampPlaceName'

export default {
  name: 'UserStampingPlaces',

  props: {
    userId: { type: [Number, String], default: null }
  },

  data () {
    return {
      T,
      loaded: false,
      locationEnabled: false,
      restrictEnabled: false,
      places: [],
      addresses: {},
      exempt: false,
      savedExempt: false,
      busy: false,
      message: '',
      failed: false
    }
  },

  computed: {
    visible () {
      return this.loaded && (this.locationEnabled || this.places.length > 0)
    }
  },

  watch: {
    userId: { immediate: true, handler () { this.load() } }
  },

  methods: {
    load () {
      this.loaded = false
      if (this.userId == null || this.userId === '') return Promise.resolve()
      const asked = this.userId
      return axios.get(process.env.VUE_APP_API_ORIGIN + '/users/' + asked + '/stamping-places')
        .then(response => {
          if (asked !== this.userId) return
          const data = (response && response.data) || {}
          this.places = Array.isArray(data.places) ? data.places : []
          this.locationEnabled = data.location_enabled === true
          this.restrictEnabled = data.restrict_enabled === true
          this.exempt = data.exempt === true
          this.savedExempt = this.exempt
          this.loaded = true
          this.places.forEach(place => this.resolveAddress(place))
        })
        .catch(() => { this.loaded = false })
    },
    keyOf (place) {
      return place.latitude + ',' + place.longitude
    },
    resolveAddress (place) {
      const key = this.keyOf(place)
      if (Object.prototype.hasOwnProperty.call(this.addresses, key)) return
      stampPlaceName(place.latitude, place.longitude)
        .then(name => { this.$set(this.addresses, key, name || '') })
    },
    addressOf (place) {
      const name = this.addresses[this.keyOf(place)]
      return name ? name + ' 付近' : ''
    },
    mapUrl (place) {
      return 'https://www.google.com/maps?q=' + Number(place.latitude) + ',' + Number(place.longitude)
    },
    kindOf (place) {
      if (place.user_id != null) return T.userKindPersonal
      if (place.project_id != null) return T.targetProjectNamed.replace('{名前}', place.project_name || '')
      return T.targetEveryone
    },
    save () {
      this.busy = true
      this.message = ''
      this.failed = false
      const exempt = this.exempt
      return axios.post(
        process.env.VUE_APP_API_ORIGIN + '/users/' + this.userId + '/stamping-restriction', { exempt })
        .then(() => {
          this.savedExempt = exempt
          this.message = T.exemptSaved
        })
        .catch(error => {
          this.failed = true
          this.message = backendErrorMessage(error, T.loadFailed)
        })
        .then(() => { this.busy = false })
    }
  }
}
</script>

<style scoped>
.user-stamping-places {
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid #d8dbe0;
}
.user-stamping-places h3 {
  font-size: 1rem;
  font-weight: 700;
}
.user-stamping-places__lead,
.user-stamping-places__empty,
.user-stamping-places small {
  color: #5c6873;
}
.user-stamping-places__table {
  overflow-x: auto;
  margin-bottom: 0.75rem;
}
.user-stamping-places__table table {
  width: 100%;
  border-collapse: collapse;
}
.user-stamping-places__table th,
.user-stamping-places__table td {
  padding: 0.4rem 0.6rem;
  border-bottom: 1px solid #e4e7ea;
  text-align: left;
}
.user-stamping-places__switch {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  max-width: 46rem;
  margin: 0.75rem 0;
  padding: 0.75rem;
  border: 1px solid #d8dbe0;
  border-radius: 0.4rem;
}
.user-stamping-places__switch input {
  margin-top: 0.3rem;
}
.user-stamping-places__switch strong,
.user-stamping-places__switch small {
  display: block;
}
.user-stamping-places__message {
  margin: 0.4rem 0;
  font-weight: 700;
  color: #2e7d5b;
}
.user-stamping-places__message--error {
  color: #c0392b;
}
</style>
